import { capitalize } from 'lodash';

import { Block } from '@/components/block';
import { Heading } from '@/components/heading';
import { Icon } from '@/components/icon';
import { Link } from '@/components/link';
import { MarkerDot } from '@/components/map-markers/marker-dot';
import { Text } from '@/components/text';
import { VESSEL_RORO } from '@/lib/constants';
import { PortPageInterface, PositionInterface } from '@/types';
import { ContactDetailGroupApiInterface, SeaportFeatureSetApiInterface } from '@/types/api-types';
import { BlockBody, BlockHeading, BlockId } from '@/types/block-types';
import { formatPortAccessFeatureSet } from '@/utils/format';
import { buildStaticMapUrl, createPortInsightsDeepLinkUrl, getPreferredIdentifier } from '@/utils/helpers';
import { cleanupExternalUrls } from '@/utils/helpers/urls';
import { getMapMarkerIcon } from '@/utils/mapping';
import { tv } from '@/utils/styles';

import { LocalClock } from './local-clock';

const getMapUrl = (location: PositionInterface | undefined | null, isSmall = false) => {
  if (!location || !location.lng) return '';
  return isSmall ? buildStaticMapUrl(location, '144x96', 3) : buildStaticMapUrl(location, '1280x800', 10);
};

const getPrimaryWebsite = (contactDetails?: ContactDetailGroupApiInterface[] | null): string => {
  const details = contactDetails?.[0]?.details[0];
  if (details && details.type === 'website') {
    return cleanupExternalUrls(details.value);
  }
  return '';
};

// Define a default for when we have no data
const defaultPortFeatureSet: SeaportFeatureSetApiInterface = { portAccess: ['road'], roro: false, portSize: 'unknown' };

export interface ContentMapBlockProps {
  id: BlockId;
  heading?: BlockHeading;
  body?: BlockBody;
  data: PortPageInterface;
}

const ContentMapBlock = ({ id, heading, body, data }: ContentMapBlockProps) => {
  const { contactDetails, details: portDetails } = data;

  // Seaport Data
  const portFeatureSet = data.seaportInfo?.featureSet || defaultPortFeatureSet;
  const portVessels = data.seaportInfo?.vesselGroups
    ? [...data.seaportInfo.vesselGroups, { label: VESSEL_RORO, available: !!portFeatureSet.roro }]
    : null;
  const primaryWebsite = getPrimaryWebsite(contactDetails);

  // Map Stuff
  const mapUrl = getMapUrl(portDetails.location);
  const miniMapUrl = getMapUrl(portDetails.location, true);
  const portIdentifiers = getPreferredIdentifier(portDetails.identifiers);
  const portType = portDetails.type;
  const portTypeName = capitalize(portType);

  const {
    base,
    mapWrapper,
    extraDetails,
    grid,
    markerDot,
    staticMap,
    miniMapContainer,
    miniStaticMap,
    miniStaticMarkerDot,
    miniStaticMapLink,
    link,
    pill,
    portFeaturePill,
    headingStyles,
  } = styles();

  return (
    <Block id={id} heading={heading} body={body} className="bg-grey-50 pt-0">
      <div className={base()}>
        <div className="p-6 md:p-12">
          <div>
            <Heading variant="h6" as="h3" className={headingStyles()}>
              {portTypeName} name
            </Heading>
            <Text className="m-0 font-medium">
              {portDetails.name} <span className={pill()}>{portDetails.portId}</span>
            </Text>
          </div>
          {primaryWebsite && (
            <div className="mt-4">
              <Heading variant="h6" as="h3" className={headingStyles()}>
                Contact details
              </Heading>
              <Link href={primaryWebsite} className={link()} target="_blank">
                {portTypeName} Website
                <Icon name="new-tab" />
              </Link>
            </div>
          )}
          <div className="mt-4">
            <Heading variant="h6" as="h3" className={headingStyles()}>
              Port type
            </Heading>
            <Text className="m-0">
              {portTypeName} {portDetails.size ? `(${capitalize(portDetails.size)})` : ''}
            </Text>
          </div>
          <div className={grid()}>
            <div>
              <Heading variant="h6" as="h3" className={headingStyles()}>
                Location
              </Heading>
              <Text className="m-0">
                {portDetails.countryName} ({portDetails.countryCode})
              </Text>
            </div>
            <div>
              <Heading variant="h6" as="h3" className={headingStyles()}>
                Coordinates
              </Heading>
              <span className={pill({ className: '-ml-2' })}>
                {portDetails.location?.lat.toFixed(3)}, {portDetails.location?.lng.toFixed(3)}
              </span>
            </div>
          </div>
          <div className={grid()}>
            <div>
              <Heading variant="h6" as="h3" className={headingStyles()}>
                Timezone
              </Heading>
              <Text className="m-0">{portDetails.timezone}</Text>
            </div>
            <div>
              <Heading variant="h6" as="h3" className={headingStyles()}>
                Local time
              </Heading>
              <span className={pill({ className: '-ml-2' })}>
                <LocalClock timezone={portDetails.timezone} />
              </span>
            </div>
          </div>
          <div className={extraDetails()}>
            <div>
              <Heading variant="h6" as="h3" className={headingStyles({ className: 'mb-2' })}>
                {portTypeName} Access
              </Heading>
              <ul>
                {formatPortAccessFeatureSet(portFeatureSet).map((featureSetitem, i) => (
                  <li
                    key={`feature-set-${i}`}
                    className={portFeaturePill({ pillIsActive: featureSetitem.available })}
                    data-cy="content-map-block-port-feature-pill"
                  >
                    {featureSetitem.label}
                  </li>
                ))}
              </ul>
            </div>
            {portVessels && (
              <div className="mt-4">
                <Heading variant="h6" as="h3" className={headingStyles({ className: 'mb-2' })}>
                  Cargo Types
                </Heading>
                <ul>
                  {portVessels.map((vessel, i) => (
                    <li
                      className={portFeaturePill({ pillIsActive: vessel.available })}
                      key={`vessel-${i}`}
                      data-cy="content-map-block-port-feature-pill"
                    >
                      {vessel.label.split('Carrier')[0]}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
        <div className={mapWrapper({ className: 'chromatic-ignore' })}>
          {mapUrl && (
            <div className={staticMap()} style={{ backgroundImage: `url(${mapUrl})` }} data-testid="map">
              <MarkerDot
                isActive
                backgroundColor="lightBlue.500"
                borderColor="lightBlue.500"
                size="2xl"
                tooltipContent={portDetails.portId}
                className={markerDot()}
              >
                <Icon name={getMapMarkerIcon(portType)} color="white" />
              </MarkerDot>
            </div>
          )}
          {miniMapUrl && portIdentifiers && (
            <Link
              href={createPortInsightsDeepLinkUrl(portDetails)}
              target="_blank"
              rel="nofollow"
              className={miniMapContainer()}
            >
              <div className={miniStaticMap()} style={{ backgroundImage: `url(${miniMapUrl})` }} data-testid="mini-map">
                <span className={miniStaticMarkerDot()} />
              </div>
              <span className={miniStaticMapLink()}>
                Enlarge Map <Icon name="new-tab" size="base" />
              </span>
            </Link>
          )}
          div
        </div>
      </div>
    </Block>
  );
};

const styles = tv({
  slots: {
    base: 'relative z-2 -mx-3 -mt-8 rounded-lg bg-white shadow-[2px_2px_8px_rgba(0,0,0,0.1)] lg:-mx-12 lg:grid lg:min-h-[500px] lg:grid-cols-[2fr_3fr]',
    mapWrapper:
      'relative w-full overflow-hidden bg-grey-200 pb-[56.25%] max-lg:rounded-b-lg lg:rounded-br-lg lg:rounded-tr-lg',
    extraDetails: 'mt-6 border-t border-grey-200 pt-6',
    headingStyles: 'm-0 text-grey-500',
    grid: 'mt-4 grid grid-cols-2',
    markerDot: 'absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 transform',
    staticMap: 'absolute bottom-0 left-0 right-0 top-0 bg-cover bg-center',
    miniMapContainer:
      'absolute bottom-2 right-2 cursor-pointer rounded-md bg-white p-0.5 pb-0 text-sm font-medium uppercase no-underline hover:shadow-lg',
    miniStaticMap: 'relative z-2 h-24 w-36 rounded-[7px] bg-cover bg-center',
    miniStaticMarkerDot:
      'absolute left-1/2 top-1/2 size-2 -translate-x-1/2 -translate-y-1/2 rounded border-2 border-lightBlue-700 bg-white',
    miniStaticMapLink: 'flex items-center justify-between px-2 py-1',
    link: 'mr-4 inline-flex items-center hover:text-lightBlue-600 [&_svg]:ml-2 [&_svg]:size-4',
    pill: 'inline-flex rounded bg-grey-50 px-2 text-baseSm font-medium text-grey-800',
    portFeaturePill:
      'mb-1 mr-2 inline-flex items-center rounded bg-grey-100 px-2 text-baseSm text-grey-350 before:mr-2 before:block before:size-2 before:rounded before:bg-grey-300',
  },
  variants: {
    pillIsActive: {
      true: {
        portFeaturePill: 'bg-blue-50 text-text-primary shadow-[1px_1px_0px_var(--colors-blue-100)] before:bg-green-400',
      },
    },
  },
});

export { ContentMapBlock };
